import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { TodoRow } from './todo-row';

import { Text } from '@/components/ui';
import { useTodoItems } from '@/hooks/use-todo-items';

/**
 * "To Do" — the dashboard summary of everything waiting on this user (#279).
 *
 * A SUMMARY. The merged, sorted list comes from `useTodoItems`; this shows the first few
 * and links to `/todo` for the rest. The full page renders identical rows from the
 * identical hook, so the two can never disagree — which is the whole reason the merge was
 * lifted out of this file.
 *
 * Self-hides when there is nothing to do: an empty to-do card trains people to skip it.
 */

/** How many rows before "…and N more". A dashboard card is a nudge, not the queue. */
const MAX_LISTED = 8;

export const TodoPanel: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const { items, overdueCount, truncated, refetch } = useTodoItems();

	if (items.length === 0) return null;

	const listed = items.slice(0, MAX_LISTED);
	const remaining = items.length - listed.length;

	return (
		<View style={[styles.card, style]} testID="v2-todo-panel">
			<View style={styles.header}>
				<Ionicons
					name={overdueCount > 0 ? 'alert-circle' : 'list-outline'}
					size={18}
					color={overdueCount > 0 ? '#c62828' : '#546e7a'}
				/>
				<Text style={styles.title}>To Do</Text>
				<View style={[styles.badge, overdueCount > 0 && styles.badgeOverdue]}>
					<Text style={styles.badgeText}>{items.length}</Text>
				</View>
				{overdueCount > 0 && (
					<Text numberOfLines={1} style={styles.overdueNote}>{overdueCount} overdue</Text>
				)}
				{/*
					A dedicated flexible spacer, not `marginLeft: 'auto'` on the link.
					`marginLeft: auto` is only reliable when nothing wraps, and this header
					sits in the narrow right-hand column — see the note on `header` below.
				*/}
				<View style={styles.spacer} />
				<Pressable onPress={() => router.push('/todo')} testID="v2-todo-view-all">
					<Text style={styles.link}>View all →</Text>
				</Pressable>
			</View>

			{listed.map((item) => (
				<TodoRow key={item.id} item={item} onChanged={refetch} />
			))}

			{remaining > 0 && (
				<Pressable onPress={() => router.push('/todo')} testID="v2-todo-more">
					{/*
						"at least" when the queue was capped: `items` is a page, so the true
						remainder is larger than what can be counted here. Printing the smaller
						number flat would contradict the tile above, which reports the server
						total — the mismatch this whole hook chain exists to avoid.
					*/}
					<Text style={styles.more}>
						…and {truncated ? `at least ${remaining}` : remaining} more
					</Text>
				</Pressable>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: '#fff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e8eaed',
		padding: 18,
	},
	/**
	 * NO `flexWrap` here, deliberately.
	 *
	 * With wrapping on, the title, badge, overdue note and "View all" together overflow
	 * this card's narrow column, so the link wrapped onto a second line — where it renders
	 * at the LEFT edge and reads as though it were left-aligned. One non-wrapping row plus
	 * a spacer keeps it pinned right at every width; the overdue note truncates instead.
	 */
	header: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
	title: { fontSize: 16, fontWeight: '700', color: '#263238' },
	spacer: { flex: 1, minWidth: 8 },
	badge: {
		backgroundColor: '#546e7a', borderRadius: 10, minWidth: 22, height: 20,
		alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6,
	},
	badgeOverdue: { backgroundColor: '#c62828' },
	badgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },
	// minWidth 0 so a long count truncates rather than squeezing the link off the row.
	overdueNote: { fontSize: 12, fontWeight: '700', color: '#c62828', minWidth: 0, flexShrink: 1 },
	link: { color: '#1976d2', fontSize: 13, fontWeight: '500' },
	more: { color: '#1976d2', fontSize: 13, paddingTop: 10 },
});
